import _ from 'lodash';

import {CalendarModel} from '../../models/api/calendarModel';
import {CalendarVersionAccessRolesEnum, CalendarVersionModel} from '../../models/api/calendarVersionModel';
import {ChannelModel} from '../../models/api/channelModel';
import {ResourceMap} from '../../store/appTypes';

export function calendarVersionCanReply(
  calendarVersion: CalendarVersionModel,
  replyChannels: ReadonlyArray<ChannelModel>,
) {
  return Boolean(
    isWriteAccessCalendarVersion(calendarVersion) &&
      replyChannels.find((c) => c.id === calendarVersion.channelId),
  );
}

const rolesWithWriteAccess = [CalendarVersionAccessRolesEnum.WRITER, CalendarVersionAccessRolesEnum.OWNER];
export function isWriteAccessCalendarVersion(calendarVersion: CalendarVersionModel) {
  return rolesWithWriteAccess.includes(calendarVersion.accessRole);
}

export function buildCalendarDisplayName(
  calendarVersion: CalendarVersionModel,
  calendars: ResourceMap<CalendarModel>,
) {
  const calendar = calendars[calendarVersion.calendarId];
  if (!calendar) {
    return '';
  }

  return calendarVersion.nameOverride || calendar.name;
}

/**
 * Sorts the calendarVersions by the calendarVersionIds in sortOrder, or in the following order:
 *      Alphabetical first, then:
 *      1) Primary Calendars you own
 *      2) Any selected Calendars
 *      3) Any non selected Calendars you own
 *      4) Any non selected Calendars you don't own
 */
export function sortCalendarVersions(
  calendarVersions: ReadonlyArray<CalendarVersionModel>,
  calendars: ResourceMap<CalendarModel>,
  selectedCalendarVersionIds: ReadonlyArray<number>,
  sortOrder?: ReadonlyArray<number>,
) {
  return _(calendarVersions)
    .compact()
    .filter((v) => Boolean(calendars[v.calendarId]))
    .sortBy((v) =>
      sortOrder ? sortOrder.indexOf(v.id) : findSortOrder(v, calendars, selectedCalendarVersionIds),
    )
    .value();
}

function findSortOrder(
  value: CalendarVersionModel,
  calendars: ResourceMap<CalendarModel>,
  selectedCalendarVersionIds: ReadonlyArray<number>,
) {
  const calendarDisplayName = buildCalendarDisplayName(value, calendars).toLowerCase();
  const isOwner = value.accessRole === CalendarVersionAccessRolesEnum.OWNER;

  // Invert booleans as sortBy gives false values precedence
  return [
    !(isOwner && value.isPrimary),
    !selectedCalendarVersionIds.includes(value.id),
    !isOwner,
    calendarDisplayName,
  ];
}
